import { useCallback, useEffect, useRef, useState } from "react";
import { openWhatsApp } from "../../lib/whatsapp";
import { useSearchParams } from "react-router-dom";
import { format, subDays } from "date-fns";
import { api } from "@/lib/hkApi";
import { toast } from "sonner";
import { Minus, Plus, Receipt, Refrigerator, TriangleAlert, Layers, MessageCircle } from "lucide-react";

export default function Reception() {
  const [rooms, setRooms] = useState([]);
  const [items, setItems] = useState([]);
  const [linen, setLinen] = useState([]);
  const [searchParams] = useSearchParams();
  const [roomId, setRoomId] = useState(searchParams.get("room_id") || null);
  const [from, setFrom] = useState(searchParams.get("from") || format(subDays(new Date(), 7), "yyyy-MM-dd"));
  const [to, setTo] = useState(searchParams.get("to") || format(new Date(), "yyyy-MM-dd"));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const autoCalc = useRef(!!searchParams.get("room_id"));

  const load = useCallback(async () => {
    try {
      const [r, f, l] = await Promise.all([api.get("/rooms"), api.get("/frigobar-items"), api.get("/linen-items")]);
      setRooms(r.data);
      setItems(f.data);
      setLinen(l.data);
    } catch { toast.error("Errore caricamento dati"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickRoom = async (id) => {
    setRoomId(id);
    setReport(null);
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const res = await api.get("/day", { params: { date: today } });
      const room = res.data.rooms.find((x) => x.id === id);
      if (room && room.stay_from) {
        setFrom(room.stay_from);
        setTo(room.status === "partenza" ? room.stay_to : today);
      }
    } catch { /* date manuali */ }
  };

  const calc = useCallback(async () => {
    if (!roomId) { toast.info("Seleziona una camera"); return; }
    setLoading(true);
    try {
      const res = await api.get("/frigobar/report", { params: { room_id: roomId, from, to } });
      setReport(res.data);
    } catch { toast.error("Errore calcolo conto"); }
    finally { setLoading(false); }
  }, [roomId, from, to]);

  // Opened from Pulizie → Oggi ("Conto frigobar" on a departing room): compute right away.
  useEffect(() => { if (autoCalc.current && rooms.length) { autoCalc.current = false; calc(); } }, [rooms, calc]);

  const setStock = async (item, delta) => {
    const next = Math.max(0, (item.stock ?? 0) + delta);
    setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, stock: next } : i));
    try { await api.put(`/frigobar-items/${item.id}/stock`, { stock: next }); }
    catch { toast.error("Errore aggiornamento magazzino"); load(); }
  };

  const setLinenStock = async (item, delta) => {
    const next = Math.max(0, (item.stock ?? 0) + delta);
    setLinen((prev) => prev.map((i) => i.id === item.id ? { ...i, stock: next } : i));
    try { await api.put(`/linen-items/${item.id}/stock`, { stock: next }); }
    catch { toast.error("Errore aggiornamento biancheria"); load(); }
  };

  const [waBusy, setWaBusy] = useState(false);
  const [guestSent, setGuestSent] = useState(false);
  const sendWa = async (party) => {
    setWaBusy(true);
    try {
      const res = await api.get("/frigobar/report/wa", { params: { room_id: roomId, from, to, party } });
      if (!res.data.wa_url) {
        toast.error(party === "guest" ? "Numero dell'ospite non disponibile nella prenotazione." : "Numero reception non configurato.");
        return;
      }
      if (party === "guest") setGuestSent(true);
      openWhatsApp(res.data.wa_url);
    } catch { toast.error("Impossibile preparare il messaggio WhatsApp"); }
    finally { setWaBusy(false); }
  };

  const roomName = rooms.find((r) => r.id === roomId)?.name || "";

  return (
    <div data-testid="reception-page" className="max-w-2xl mx-auto px-4 -mt-6 space-y-6">
      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
          <Receipt className="h-5 w-5 text-[#4A1C17]" /> Addebito frigo bar
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">Seleziona la camera e il periodo del soggiorno: il conto include tutti i prodotti segnati "Consumato".</p>

        <div className="mt-3 flex gap-2 flex-wrap">
          {rooms.map((r) => (
            <button
              key={r.id}
              data-testid={`reception-room-${r.id}`}
              onClick={() => pickRoom(r.id)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-all border ${
                roomId === r.id ? "bg-[#4A1C17] text-[#F7EBDF] border-[#4A1C17]" : "bg-white text-[#2B1410] border-[#E8DCC8] hover:border-[#4A1C17]/40"
              }`}
            >
              {r.number}. {r.name}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input data-testid="reception-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-full border border-[#E8DCC8] bg-white px-3 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]" />
          <span className="text-[#7C5A4E] text-sm">→</span>
          <input data-testid="reception-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-full border border-[#E8DCC8] bg-white px-3 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]" />
          <button data-testid="reception-calc-btn" onClick={calc} disabled={loading} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-50">
            Calcola conto
          </button>
        </div>

        {report && (
          <div className="mt-4" data-testid="reception-report">
            {report.items.length === 0 ? (
              <p className="text-sm text-[#7C5A4E] italic py-2" data-testid="reception-empty">Nessuna consumazione nel periodo selezionato per {roomName}.</p>
            ) : (
              <>
                <p className="text-xs font-semibold text-[#7C5A4E] uppercase tracking-wide mb-2">
                  Conto {roomName} · {from} → {to}
                </p>
                <div className="space-y-1.5">
                  {report.items.map((it) => (
                    <div key={it.item_id} data-testid={`reception-row-${it.item_id}`} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8] text-sm">
                      <span className="flex-1 text-[#2B1410]">{it.name}</span>
                      <span className="text-[#7C5A4E]">{it.qty} × {it.price}</span>
                      <span className="font-semibold text-[#2B1410] w-16 text-right">{it.subtotal.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between rounded-xl bg-[#4A1C17] text-[#F7EBDF] px-4 py-3" data-testid="reception-total">
                  <span className="font-display text-lg font-bold">Totale da addebitare</span>
                  <span className="font-display text-2xl font-bold">{report.total.toFixed(2)} €</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button data-testid="reception-wa-guest" onClick={() => sendWa("guest")} disabled={waBusy}
                    className={`inline-flex items-center justify-center gap-2 rounded-full text-white px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${guestSent ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                    <MessageCircle className="h-4 w-4" /> {guestSent ? "Inviato all'ospite ✓" : "WhatsApp ospite"}
                  </button>
                  <button data-testid="reception-wa-reception" onClick={() => sendWa("reception")} disabled={waBusy}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50">
                    <MessageCircle className="h-4 w-4" /> WhatsApp reception
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-[#7C5A4E]">Il testo si modifica in Admin → Impostazioni → Template WhatsApp (Frigobar).</p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
          <Refrigerator className="h-5 w-5 text-[#4A1C17]" /> Magazzino frigo bar
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">Ogni "Rifornito" segnato dal personale scala 1 unità dalla scorta. Scorte basse evidenziate in rosso.</p>
        <div className="mt-3 space-y-1.5" data-testid="stock-list">
          {items.map((item) => {
            const stock = item.stock ?? 0;
            const low = stock <= 2;
            return (
              <div key={item.id} data-testid={`stock-row-${item.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${low ? "bg-red-50 border-red-200" : "bg-[#FAF3EE] border-[#E8DCC8]"}`}>
                <span className="text-sm flex-1 min-w-0 text-[#2B1410]">
                  {item.name}
                  {low && (
                    <span data-testid={`low-stock-${item.id}`} className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700">
                      <TriangleAlert className="h-3 w-3" /> Scorta bassa
                    </span>
                  )}
                </span>
                <button data-testid={`stock-minus-${item.id}`} onClick={() => setStock(item, -1)} className="h-7 w-7 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span data-testid={`stock-value-${item.id}`} className={`w-8 text-center text-sm font-bold ${low ? "text-red-700" : "text-[#2B1410]"}`}>{stock}</span>
                <button data-testid={`stock-plus-${item.id}`} onClick={() => setStock(item, 1)} className="h-7 w-7 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
          <Layers className="h-5 w-5 text-[#4A1C17]" /> Magazzino biancheria
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">Scorta di pulito in magazzino, <strong>esclusa</strong> la biancheria normalmente presente nelle camere (quella la vedi nella colonna "In camere"). Quando la governante segna "Messo pulito" la scorta scende da sola; quando la lavanderia consegna il pulito, aggiungilo qui.</p>
        <div className="mt-3 space-y-1.5" data-testid="linen-stock-list">
          {linen.map((item) => {
            const stock = item.stock ?? 0;
            const low = stock <= 2;
            return (
              <div key={item.id} data-testid={`linen-stock-row-${item.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${low ? "bg-red-50 border-red-200" : "bg-[#FAF3EE] border-[#E8DCC8]"}`}>
                <span className="text-sm flex-1 min-w-0 text-[#2B1410]">
                  {item.name}
                  <span className="text-xs text-[#7C5A4E] ml-1.5">In camere: {item.in_camere ?? 0}</span>
                  {low && (
                    <span data-testid={`linen-low-${item.id}`} className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700">
                      <TriangleAlert className="h-3 w-3" /> Scorta bassa
                    </span>
                  )}
                </span>
                <button data-testid={`linen-stock-minus-${item.id}`} onClick={() => setLinenStock(item, -1)} className="h-7 w-7 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span data-testid={`linen-stock-value-${item.id}`} className={`w-8 text-center text-sm font-bold ${low ? "text-red-700" : "text-[#2B1410]"}`}>{stock}</span>
                <button data-testid={`linen-stock-plus-${item.id}`} onClick={() => setLinenStock(item, 1)} className="h-7 w-7 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
